// challenge-path-labeling — code-challenge-dotted-path-checkpoints.
//
// ONE shared, pure (no DOM, no network) module for the discovered-path pipeline:
// change-detection ranking, near-duplicate collapsing, rough-heuristic friendly
// labeling (NO LLM — owner decision), and the naming-variety fallback matcher.
//
// Imported by BOTH the teacher-facing record chrome (challenge-record-chrome.js,
// to generate `pathLabel` at record time) AND the student-facing Check flow
// (challenge-chrome.js, to generate comparable candidate labels for fallback
// matching at Check time). It is NOT colocated inside either — challenge-record-
// chrome.js is teacher-only + dynamically imported and unreachable from the
// student bundle, so two independent ports would risk silent accuracy drift with
// no automated gate to catch it (decisions §"One shared labeling module").
//
// JS port of the validated spike design (dump/trello/QCbdiYN2/spike/labeling/
// rank_label.py + role-matching/match.py) — the DESIGN, not literal code.

// This module is dependency-free by contract: scripts/runner-smoke/smoke-games.js
// loads it via a `data:` URL (the Node-ESM trick), which has no hierarchical base,
// so a relative `import` specifier here would be unresolvable and break the smoke
// harness. The numeric-vs-discrete channel predicate `isDiscreteKind` is therefore
// INJECTED into resolvePathFallback by callers (which already import it from
// challenge-grading.js) rather than imported here — keeping the fallback leafType
// guard and the grader in lock-step without a static import.

var COLLAPSE_THRESHOLD = 3;   // spike-validated near-duplicate collapse floor
var LEAF_WEIGHT = 0.75;       // spike-validated leaf-vs-chain word weighting
var MATCH_THRESHOLD = 0.5;    // spike-validated fallback similarity threshold

// Leaf/role concept normalization table (spike-validated coverage).
var SYNONYMS = {
  x: 'x position', y: 'y position',
  posx: 'x position', posy: 'y position', pos_x: 'x position', pos_y: 'y position',
  dx: 'x velocity', dy: 'y velocity', vx: 'x velocity', vy: 'y velocity',
  hp: 'health', health: 'health', lives: 'lives', score: 'score', speed: 'speed'
};

// A path token is either an identifier (`player`, `x`) or a bracket segment
// (`[0]`, `['score']`).
var TOKEN_RE = /[A-Za-z_]\w*|\[[^\]]*\]/g;

function splitWords(identifier) {
  var s = String(identifier).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  s = s.replace(/_/g, ' ');
  return s.toLowerCase().split(/\s+/).filter(function (w) { return w; });
}

export function humanizeWord(identifier) {
  var key = String(identifier).toLowerCase();
  if (Object.prototype.hasOwnProperty.call(SYNONYMS, key)) { return SYNONYMS[key]; }
  var words = splitWords(identifier);
  return words.length ? words.join(' ') : String(identifier);
}

function singularize(word) {
  return (word.length > 1 && word.charAt(word.length - 1) === 's') ? word.slice(0, -1) : word;
}

export function tokenizePath(path) {
  var out = String(path == null ? '' : path).match(TOKEN_RE);
  return out || [];
}

function buildRoleWords(chainTokens) {
  var parts = [];
  chainTokens.forEach(function (tok) {
    if (tok.charAt(0) === '[') {
      var inner = tok.slice(1, -1);
      if (inner.charAt(0) === "'" || inner.charAt(0) === '"') {
        parts.push('the ' + humanizeWord(inner.replace(/^['"]|['"]$/g, '')));
      } else if (parts.length) {
        var prev = parts.pop();
        var base = prev.replace(/^(the |a |an )/, '');
        var singular = singularize(base);
        var article = 'aeiou'.indexOf(singular.charAt(0).toLowerCase()) >= 0 ? 'an' : 'a';
        parts.push(article + ' ' + singular);
      } else {
        parts.push('an item');
      }
    } else {
      parts.push(humanizeWord(tok));
    }
  });
  return parts;
}

// Rough friendly label for a path — role chain (from path segments) + humanized
// leaf attr. Grammar is intentionally crude (string concatenation, no LLM); the
// point is that DIFFERENT nesting produces DIFFERENT labels.
// Capitalize the first letter so an auto-generated label reads as a title/subject
// (e.g. "the player x position" -> "The player x position"). A teacher can still
// edit it to anything, including lowercase.
function capFirst(s) {
  return (typeof s === 'string' && s.length) ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function friendlyLabel(path) {
  var tokens = tokenizePath(path);
  if (!tokens.length) { return String(path || ''); }
  var leafTok = tokens[tokens.length - 1];
  var chainTokens = tokens.slice(0, -1);

  var leafWord;
  if (leafTok.charAt(0) === '[') {
    var inner = leafTok.slice(1, -1);
    leafWord = (inner.charAt(0) === "'" || inner.charAt(0) === '"')
      ? humanizeWord(inner.replace(/^['"]|['"]$/g, '')) : 'value';
  } else {
    leafWord = humanizeWord(leafTok);
  }

  if (!chainTokens.length) { return capFirst(leafWord); }

  var roleWords = buildRoleWords(chainTokens);
  var outParts = roleWords.map(function (p, i) {
    if (i === 0 && !/^(the |a |an )/.test(p)) { return 'the ' + p; }
    return p;
  });
  return capFirst((outParts.join(' ') + ' ' + leafWord).trim());
}

// Replace numeric list indices with [*] so `enemies[0].x`..`enemies[5].x` group
// under one template. String dict keys are NOT collapsed (usually semantically
// distinct, e.g. state['score'] vs state['lives']).
export function collapseTemplate(path) {
  return String(path).replace(/\[\d+\]/g, '[*]');
}

// Rank + collapse + label a set of discovered candidates.
//   discovered: [{ path, leafType, changed }]  (`changed` computed by the caller)
// Returns grouped results, changed-first:
//   [{ template, representativePath, changed, label, collapsed, collapsedCount, memberPaths, leafType }]
export function rankAndLabel(discovered) {
  var groups = {};
  (discovered || []).forEach(function (d) {
    if (!d || typeof d.path !== 'string') { return; }
    var tmpl = collapseTemplate(d.path);
    (groups[tmpl] = groups[tmpl] || []).push(d);
  });

  var results = Object.keys(groups).map(function (tmpl) {
    var members = groups[tmpl].slice().sort(function (a, b) {
      return a.path < b.path ? -1 : (a.path > b.path ? 1 : 0);
    });
    var rep = members[0];
    var collapsed = members.length >= COLLAPSE_THRESHOLD && tmpl.indexOf('[*]') >= 0;
    return {
      template: tmpl,
      representativePath: rep.path,
      leafType: rep.leafType,
      changed: members.some(function (m) { return !!m.changed; }),
      label: friendlyLabel(rep.path),
      collapsed: collapsed,
      collapsedCount: collapsed ? members.length : 1,
      memberPaths: members.map(function (m) { return m.path; })
    };
  });

  results.sort(function (a, b) {
    var ca = a.changed ? 0 : 1, cb = b.changed ? 0 : 1;
    if (ca !== cb) { return ca - cb; }
    return a.template < b.template ? -1 : (a.template > b.template ? 1 : 0);
  });
  return results;
}

// --- Naming-variety fallback matcher (JS port of match.py's best-of-both) -----

function labelTokens(label) {
  var m = String(label || '').toLowerCase().match(/[a-z]+/g);
  var set = {};
  (m || []).forEach(function (w) { set[w] = 1; });
  return set;
}

function jaccard(a, b) {
  var ak = Object.keys(a), bk = Object.keys(b);
  if (!ak.length && !bk.length) { return 1.0; }
  if (!ak.length || !bk.length) { return 0.0; }
  var inter = 0;
  ak.forEach(function (k) { if (b[k]) { inter++; } });
  var union = ak.length + bk.length - inter;
  return union ? inter / union : 0.0;
}

function leafAndChainWords(path) {
  var tokens = tokenizePath(path);
  var leafTok = tokens.length ? tokens[tokens.length - 1] : '';
  var chainToks = tokens.slice(0, -1);
  var leafWord;
  if (leafTok.charAt(0) === '[') {
    var inner = leafTok.slice(1, -1);
    leafWord = (inner.charAt(0) === "'" || inner.charAt(0) === '"')
      ? humanizeWord(inner.replace(/^['"]|['"]$/g, '')) : 'value';
  } else {
    leafWord = humanizeWord(leafTok);
  }
  var chainWords = {};
  chainToks.forEach(function (tok) {
    if (tok.charAt(0) === '[') { return; } // index tokens carry no role-word signal
    var hw = labelTokens(humanizeWord(tok));
    Object.keys(hw).forEach(function (k) { chainWords[k] = 1; });
  });
  return { leaf: labelTokens(leafWord), chain: chainWords };
}

function bestBy(candidates, scorer) {
  var best = null, bestScore = 0.0;
  (candidates || []).forEach(function (c) {
    var s = scorer(c);
    if (best === null || s > bestScore) { best = c; bestScore = s; }
  });
  return { match: best, score: best === null ? 0.0 : bestScore };
}

// The best-of-both fallback measure: the max of (a) token-overlap between the two
// LABEL strings, and (b) leaf-weighted word-overlap between the two raw PATH
// strings — computed per candidate, then the best-scoring candidate overall.
// Returns { match, score, stage } — stage is 'label-match' when score >= threshold,
// else 'inconclusive' (caller marks the checkpoint skipped).
export function matchFallback(teacherPath, teacherLabel, candidates, threshold) {
  threshold = typeof threshold === 'number' ? threshold : MATCH_THRESHOLD;
  var tLabelTokens = labelTokens(teacherLabel);
  var tWords = leafAndChainWords(teacherPath);
  var picked = bestBy(candidates, function (c) {
    var byLabel = jaccard(tLabelTokens, labelTokens(c.label));
    var cWords = leafAndChainWords(c.representativePath);
    var byPath = LEAF_WEIGHT * jaccard(tWords.leaf, cWords.leaf)
      + (1 - LEAF_WEIGHT) * jaccard(tWords.chain, cWords.chain);
    return Math.max(byLabel, byPath);
  });
  if (picked.match && picked.score >= threshold) {
    return { match: picked.match, score: picked.score, stage: 'label-match' };
  }
  return { match: null, score: picked.score, stage: 'inconclusive' };
}

// --- High-level discovery pipeline (used by both chromes + the smoke harness) --

// A candidate's value CHANGED during the demo when its per-frame series has at
// least two distinct non-null values (numeric or discrete).
export function pathChanged(path, valueSeries, discreteSeries) {
  var series = (valueSeries && valueSeries[path]) || (discreteSeries && discreteSeries[path]) || null;
  if (!Array.isArray(series)) { return false; }
  var first = null, seen = false;
  for (var i = 0; i < series.length; i++) {
    var v = series[i];
    if (v === null || v === undefined) { continue; }
    if (!seen) { first = v; seen = true; }
    else if (v !== first) { return true; }
  }
  return false;
}

// Turn a runner discovery result into ranked/labeled candidate groups.
//   discovery: { discoveredPaths: [{path, leafType}], series, discreteSeries }
export function discoverCandidates(discovery) {
  discovery = discovery || {};
  var paths = discovery.discoveredPaths || [];
  var withChanged = paths.map(function (p) {
    return {
      path: p.path,
      leafType: p.leafType,
      changed: pathChanged(p.path, discovery.series, discovery.discreteSeries)
    };
  });
  return rankAndLabel(withChanged);
}

// Resolve a path-temporal checkpoint against a student's own discovery result via
// the naming-variety fallback. Returns { path } (the candidate representative path
// whose series should grade the checkpoint) when a candidate clears the threshold,
// else null (caller marks the checkpoint skipped). Pure — reused by the smoke
// harness so the fallback chain is regression-tested through the real runner.
export function resolvePathFallback(checkpoint, discovery, threshold, isDiscreteKind) {
  var candidates = discoverCandidates(discovery);
  var m = matchFallback(checkpoint.path, checkpoint.pathLabel || checkpoint.path, candidates, threshold);
  if (!m.match) { return null; }
  // Cross-type guard: comparePathTemporal grades a numeric pathCheck.type from
  // valueSeries[path] and a discrete one (flag-becomes/state-sequence) from
  // discreteSeries[path]. A best-scoring candidate whose leaf lives in the WRONG
  // channel would leave the graded channel all-null → the evaluator returns a
  // punitive FAIL instead of the intended skip. So a cross-type match is NOT a
  // match: fall through to the inconclusive/skipped result exactly as if nothing
  // cleared the threshold.
  var checkType = checkpoint.pathCheck && checkpoint.pathCheck.type;
  if (!leafTypeMatchesChannel(m.match.leafType, checkType, isDiscreteKind)) { return null; }
  return { path: m.match.representativePath, score: m.score };
}

// Is a discovered candidate's leaf in the same numeric-vs-discrete channel that
// comparePathTemporal will grade this pathCheck.type from? Numeric leaves are
// int/float (→ valueSeries); discrete leaves are bool/str (→ discreteSeries).
// isDiscreteKind is INJECTED (the SAME predicate comparePathTemporal uses to pick
// the channel), so the guard and the grader stay in lock-step. Defensive: if it's
// not a function (an old caller that didn't pass it), skip the cross-type guard
// and treat the candidate as compatible rather than throwing.
function leafTypeMatchesChannel(leafType, pathCheckType, isDiscreteKind) {
  if (typeof isDiscreteKind !== 'function') { return true; }
  var numericLeaf = leafType === 'int' || leafType === 'float';
  var discreteLeaf = leafType === 'bool' || leafType === 'str';
  return isDiscreteKind(pathCheckType) ? discreteLeaf : numericLeaf;
}

export { COLLAPSE_THRESHOLD, LEAF_WEIGHT, MATCH_THRESHOLD };
